import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { Send, Sparkles, FileText, Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const ChatPage = ({ user, setUser }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    createNewSession();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const createNewSession = async () => {
    try {
      const res = await axios.post(`${API_URL}/chat/new-session`);
      setSessionId(res.data.session_id);
    } catch (error) {
      toast.error('Failed to create chat session');
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/chat`, {
        message: userMessage,
        session_id: sessionId
      });

      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: res.data.response,
          document_references: res.data.document_references
        }
      ]);
    } catch (error) {
      toast.error('Failed to get response from AI');
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="chat-page">
        {/* Header */}
        <div className="px-8 py-4 border-b border-zinc-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-violet-600 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                AI Chat Assistant
              </h1>
              <p className="text-sm text-zinc-600">Ask me anything about your knowledge base</p>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-8 bg-zinc-50" data-testid="chat-messages">
          <div className="max-w-3xl mx-auto space-y-6">
            {messages.length === 0 && (
              <div className="text-center py-12" data-testid="chat-empty-state">
                <div className="h-16 w-16 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="h-8 w-8 text-violet-600" />
                </div>
                <h2 className="text-xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Welcome to AI Assistant
                </h2>
                <p className="text-zinc-600 mb-6">Ask me questions about your documents and I'll help you find answers.</p>
                <div className="flex flex-wrap justify-center gap-3">
                  <button
                    onClick={() => setInput('What documents do I have?')}
                    className="px-4 py-2 bg-white border border-zinc-200 rounded-lg hover:border-violet-300 hover:bg-violet-50 transition-all duration-200 text-sm"
                    data-testid="suggestion-1"
                  >
                    What documents do I have?
                  </button>
                  <button
                    onClick={() => setInput('Summarize the latest document')}
                    className="px-4 py-2 bg-white border border-zinc-200 rounded-lg hover:border-violet-300 hover:bg-violet-50 transition-all duration-200 text-sm"
                    data-testid="suggestion-2"
                  >
                    Summarize the latest document
                  </button>
                </div>
              </div>
            )}

            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`chat-message-${index}`}
              >
                {message.role === 'assistant' && (
                  <div className="h-10 w-10 rounded-full bg-violet-600 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[70%] rounded-lg p-4 ${
                    message.role === 'user'
                      ? 'bg-zinc-900 text-white'
                      : 'bg-white border border-zinc-200 text-zinc-900'
                  }`}
                >
                  <div className="prose prose-sm max-w-none prose-p:my-2 prose-headings:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </div>
                  {message.document_references && message.document_references.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-zinc-200 space-y-2">
                      <div className="text-xs text-zinc-500 font-semibold">Referenced Documents:</div>
                      {message.document_references.map((doc) => (
                        <Link
                          key={doc.id}
                          to={`/documents/${doc.id}`}
                          className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-700"
                          data-testid={`doc-reference-${doc.id}`}
                        >
                          <FileText className="h-4 w-4" />
                          <span>{doc.title}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                {message.role === 'user' && (
                  <div className="h-10 w-10 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-zinc-700 font-semibold">{user.name.charAt(0)}</span>
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-4" data-testid="chat-loading">
                <div className="h-10 w-10 rounded-full bg-violet-600 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div className="bg-white border border-zinc-200 rounded-lg p-4">
                  <Loader className="h-5 w-5 text-violet-600 animate-spin" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input */}
        <div className="p-6 border-t border-zinc-200 bg-white">
          <form onSubmit={handleSend} className="max-w-3xl mx-auto">
            <div className="flex gap-3">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask a question about your knowledge base..."
                className="flex-1"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="chat-send-button"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default ChatPage;